import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, FileIcon, FolderIcon, History, Pencil, Shield, Trash2 } from 'lucide-react';
import type { WorkGroupContentsResponse, WorkGroupFileListItem, WorkGroupFolderMetadata } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/toaster';
import { usePermissions } from '@/hooks/use-permissions';
import {
  useDownloadWorkGroupFile,
  useTrashWorkGroupFile,
  isConflictCopyDisplayName,
  isWorkGroupPreviewableMimeType,
  workgroupsMutationErrorMessage,
} from '@/hooks/use-workgroups';
import { DeleteFolderDialog } from '@/features/workgroups/components/delete-folder-dialog';
import { RenameFolderDialog } from '@/features/workgroups/components/rename-folder-dialog';
import { VersionHistoryDialog } from '@/features/workgroups/components/version-history-dialog';

interface WorkGroupContentsListProps {
  workGroupId: string;
  data: WorkGroupContentsResponse | undefined;
  privateAccessReason?: string;
  className?: string;
  onManageFolderPermissions?: (folder: WorkGroupFolderMetadata) => void;
}

export function WorkGroupContentsList({
  workGroupId,
  data,
  privateAccessReason,
  className,
  onManageFolderPermissions,
}: WorkGroupContentsListProps) {
  const { hasCapability, hasAnyCapability } = usePermissions();
  const downloadMutation = useDownloadWorkGroupFile();
  const trashFileMutation = useTrashWorkGroupFile();

  const [renameFolder, setRenameFolder] = useState<WorkGroupFolderMetadata | null>(null);
  const [deleteFolder, setDeleteFolder] = useState<WorkGroupFolderMetadata | null>(null);
  const [versionFile, setVersionFile] = useState<WorkGroupFileListItem | null>(null);

  const canDownload = hasCapability('workgroups.file.download');
  const canViewVersions = hasCapability('workgroups.file.versions.view');
  const canEditFolder = hasCapability('workgroups.folder.edit');
  const canDeleteFolder = hasCapability('workgroups.folder.delete');
  const canTrashFile = hasAnyCapability([
    'workgroups.file.delete.own',
    'workgroups.file.delete.any',
  ]);
  const canManageFolderPermissions = hasCapability('workgroups.folder.permissions.manage');

  if (!data) {
    return null;
  }

  const folders = data.childFolders.filter((f) => !f.isRoot);
  const parentFolderId = data.folder.isRoot ? undefined : data.folder.id;
  const atMaxFolderDepth = data.canCreateChild === false;

  const handleFileOpenOrDownload = (file: WorkGroupFileListItem) => {
    const fileName = file.displayName?.trim() || file.originalName;
    downloadMutation.mutate(
      {
        workGroupId,
        fileId: file.id,
        fileName,
        privateAccessReason,
      },
      {
        onError: (err) => {
          toast({ title: workgroupsMutationErrorMessage(err), variant: 'destructive' });
        },
      },
    );
  };

  return (
    <div className={cn('space-y-4', className)}>
      <div className="text-sm text-muted-foreground">
        Effective permission: <span className="text-foreground">{data.effectivePermission}</span>
        {atMaxFolderDepth ? (
          <span className="mt-1 block text-destructive">
            This folder is already at the maximum nesting depth. Create a folder higher up to continue
            organizing files.
          </span>
        ) : null}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <section className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">Folders</h3>
          {folders.length === 0 ? (
            <p className="text-sm text-muted-foreground">No subfolders in this location.</p>
          ) : (
            <ul className="space-y-1">
              {folders.map((folder) => (
                <li
                  key={folder.id}
                  className="flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-accent/50"
                >
                  <Link
                    to={`/workgroups/${workGroupId}/folders/${folder.id}`}
                    className="flex min-w-0 flex-1 items-center gap-2 px-1 py-1 text-sm text-foreground"
                  >
                    <FolderIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <span className="truncate">{folder.name}</span>
                  </Link>
                  {canEditFolder ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      aria-label={`Rename ${folder.name}`}
                      onClick={() => setRenameFolder(folder)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                  {canManageFolderPermissions && onManageFolderPermissions ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      aria-label={`Permissions for ${folder.name}`}
                      onClick={() => onManageFolderPermissions(folder)}
                    >
                      <Shield className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                  {canDeleteFolder ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-destructive"
                      aria-label={`Delete ${folder.name}`}
                      onClick={() => setDeleteFolder(folder)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="space-y-2">
          <h3 className="text-sm font-medium text-foreground">Files</h3>
          {data.files.length === 0 ? (
            <p className="text-sm text-muted-foreground">No files in this folder.</p>
          ) : (
            <ul className="space-y-1">
              {data.files.map((file) => {
                const fileLabel = file.displayName?.trim() || file.originalName;
                const isConflictCopy = isConflictCopyDisplayName(fileLabel);
                return (
                <li
                  key={file.id}
                  className="flex items-center gap-1 rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-accent/50"
                >
                  <FileIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{fileLabel}</span>
                  {isConflictCopy ? (
                    <Badge variant="outline" className="shrink-0 font-normal" data-testid="wg-conflict-copy-badge">
                      Conflict copy
                    </Badge>
                  ) : null}
                  {canDownload && isWorkGroupPreviewableMimeType(file.mimeType) ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      aria-label={`Open preview for ${file.originalName}`}
                      title="Open preview in a new tab when supported"
                      disabled={downloadMutation.isPending}
                      onClick={() => handleFileOpenOrDownload(file)}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                  {canDownload ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      aria-label={`Download ${file.originalName}`}
                      disabled={downloadMutation.isPending}
                      onClick={() => handleFileOpenOrDownload(file)}
                    >
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                  {canViewVersions ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      aria-label={`Version history for ${file.originalName}`}
                      onClick={() => setVersionFile(file)}
                    >
                      <History className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                  {canTrashFile ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-destructive"
                      aria-label={`Move ${file.originalName} to trash`}
                      disabled={trashFileMutation.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Move "${file.displayName?.trim() || file.originalName}" to trash?`,
                          )
                        ) {
                          trashFileMutation.mutate(
                            {
                              workGroupId,
                              fileId: file.id,
                              folderId: data.folder.id,
                              privateAccessReason,
                            },
                            {
                              onError: (err) => {
                                toast({
                                  title: workgroupsMutationErrorMessage(err),
                                  variant: 'destructive',
                                });
                              },
                            },
                          );
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  ) : null}
                </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      {renameFolder ? (
        <RenameFolderDialog
          workGroupId={workGroupId}
          folderId={renameFolder.id}
          folderName={renameFolder.name}
          parentId={parentFolderId ?? data.folder.parentId}
          privateAccessReason={privateAccessReason}
          open={!!renameFolder}
          onOpenChange={(open) => {
            if (!open) setRenameFolder(null);
          }}
        />
      ) : null}

      {deleteFolder ? (
        <DeleteFolderDialog
          workGroupId={workGroupId}
          folderId={deleteFolder.id}
          folderName={deleteFolder.name}
          parentId={parentFolderId ?? data.folder.parentId}
          privateAccessReason={privateAccessReason}
          open={!!deleteFolder}
          onOpenChange={(open) => {
            if (!open) setDeleteFolder(null);
          }}
        />
      ) : null}

      {versionFile ? (
        <VersionHistoryDialog
          workGroupId={workGroupId}
          fileId={versionFile.id}
          fileName={versionFile.displayName?.trim() || versionFile.originalName}
          folderId={data.folder.id}
          privateAccessReason={privateAccessReason}
          open={!!versionFile}
          onOpenChange={(open) => {
            if (!open) setVersionFile(null);
          }}
        />
      ) : null}
    </div>
  );
}
